import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stage } from "react-konva";

import { useImage } from "@common/util/useImage";
import { isTableLabel } from "../helpers";
import { Dispatch, State, ZoomLevel } from "../StateManager/state";
import { ZoneDimensions, ZoneWithCells } from "../types";
import { KeyboardHandler } from "./KeyboardHandler";
import { MouseHandler } from "./MouseHandler";
import { PageImage } from "./PageImage";
import { Selection } from "./Selection";
import { TableCellLinks } from "./TableCellLinks";
import { TableCellZoning } from "./TableCellZoning";
import { usePageDimensions } from "./usePageDimensions";
import { Zones } from "./Zones";

type Props = Readonly<{
  autoShrink: boolean;
  disabled: boolean;
  dispatch: Dispatch;
  height: number;
  imageURI: string;
  onSave: () => void;
  parentScrollRef: HTMLElement | null;
  resetZoom: () => void;
  state: State;
  width: number;
  zoom: ZoomLevel;
}>;

export const Canvas = ({
  autoShrink,
  disabled,
  dispatch,
  height,
  imageURI,
  onSave,
  parentScrollRef,
  resetZoom,
  state,
  width,
  zoom,
}: Props) => {
  const page = state.pages[state.activePage];
  const { activeZones, currentAction, dimensions, hoveredZone, selection } = page;
  const containerSize = useMemo(() => ({ height: height * zoom, width: width * zoom }), [
    height,
    width,
    zoom,
  ]);
  const [imageSize, offset, scale] = usePageDimensions(containerSize, dimensions);
  const [image, status] = useImage(imageURI, true, true);
  const [originalCells, setOriginalCells] = useState<ReadonlyArray<ZoneDimensions> | null>(null);
  useEffect(() => {
    if (!parentScrollRef || status !== "loaded" || image?.src !== imageURI) {
      return;
    }
    parentScrollRef.scrollTo((parentScrollRef.scrollWidth - parentScrollRef.clientWidth) / 2, 0);
  }, [imageURI, status]);
  const tableZones = useMemo(
    (): Array<ZoneWithCells> =>
      state.pages[state.activePage].zones.filter(
        z => isTableLabel(z.label, state.taxonomy) && Array.isArray(z.cells),
      ) as Array<ZoneWithCells>,
    [state.activePage, state.pages],
  );
  const setEditTable = (index : number | null) => {
    dispatch({
      zoneIndex: index,
      type: "SET EDITING TABLE"
    })
  }
  useEffect(() => {
    setEditTable(null);
  }, [state.activePage]);
  const onDoneEditTable = useCallback(() => {
    setEditTable(null);
    setOriginalCells(null);
  }, [resetZoom, setEditTable, setOriginalCells]);
  const onPopulateTableCells = useCallback(
    (cells: ReadonlyArray<ZoneDimensions>) => {
      if (state.editTable === null) {
        return;
      }
      dispatch({
        cells,
        type: "SET TABLE CELLS",
        zoneIndex: state.editTable,
      });
    },
    [state.editTable, dispatch],
  );
  const onCancelEditTable = useCallback(() => {
    if (Array.isArray(originalCells)) {
      onPopulateTableCells(originalCells);
    }
    onDoneEditTable();
  }, [onDoneEditTable, onPopulateTableCells, originalCells]);
  const onClickCellsLink = useCallback(
    (zone: ZoneWithCells) => {
      setEditTable(page.zones.indexOf(zone));
      resetZoom();
      setOriginalCells(zone.cells!);
    },
    [page.zones, resetZoom, setEditTable, setOriginalCells],
  );

  if (isNaN(containerSize.height) || isNaN(containerSize.width)) {
    return null;
  } else if (image === undefined || status !== "loaded" || image.src !== imageURI) {
    const loadingStyles = {
      alignItems: "center",
      backgroundColor: "white",
      display: "flex",
      height: `${Math.floor(imageSize.height / zoom)}px`,
      justifyContent: "center",
      margin: "auto",
      width: `${Math.floor(imageSize.width / zoom)}px`,
    };
    const spinnerStyles = { height: "5rem", width: "5rem" };
    return (
      <div style={loadingStyles}>
        <div>
          <div className="ml-auto mr-auto spinner-border" role="status" style={spinnerStyles}>
            <span className="sr-only">Loading...</span>
          </div>
          <div className="text-muted mt-3">Loading &hellip;</div>
        </div>
      </div>
    );
  } else if (image && state.editTable !== null) {
    return (
      <TableCellZoning
        containerHeight={height}
        containerWidth={width}
        onCancel={onCancelEditTable}
        onSubmit={onDoneEditTable}
        onUpdateCells={onPopulateTableCells}
        pageImage={image}
        review={disabled}
        zone={page.zones[state.editTable] as ZoneWithCells}
        zoom={state.tableZoom}
      />
    );
  }
  return (
    <>
      <Stage {...containerSize}>
        <PageImage containerSize={containerSize} image={image} offset={offset} size={imageSize} />
        <Zones
          activeZones={activeZones}
          currentAction={currentAction}
          hoveredZone={hoveredZone}
          offset={offset}
          pageHeight={page.dimensions.height}
          scale={scale}
          taxonomy={state.taxonomy}
          zones={page.zones}
          showZoneLabels={state.showZoneLabels}
        />
        <Selection offset={offset} scale={scale} selection={selection} />
        <MouseHandler
          autoShrink={autoShrink}
          disableActions={disabled}
          dispatch={dispatch}
          offset={offset}
          scale={scale}
          size={imageSize}
          state={state}
        />
        {!disabled && 
          <KeyboardHandler 
          dispatch={dispatch} 
          onSave={onSave} 
          scale={scale}
          size={imageSize}
          />
        }
      </Stage>
      <TableCellLinks
        height={height}
        offset={offset}
        scale={scale}
        setEditTable={onClickCellsLink}
        tableZones={tableZones}
        width={width}
      />
    </>
  );
};
